import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import AdminLayout from '../components/AdminLayout';
import { Trash2, Edit, Plus, AlertCircle, Search, X, CheckCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import { adminAPI } from '../../utils/api';

export default function AdminUsers() {
  const navigate = useNavigate();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const user = useAuthStore((state) => state.user);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    role: 'user',
    password: '',
  });
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  const { t } = useTranslation();

  useEffect(() => {
    if (!isAuthenticated || user?.role !== 'admin') {
      navigate('/login');
      return;
    }
    fetchUsers();
  }, [isAuthenticated, user, navigate]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getUsers();
      setUsers(response.data.data || []);
    } catch (error) {
      console.error('[AdminUsers] Failed to fetch users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm(t('Are you sure you want to delete this user?'))) return;
    try {
      await adminAPI.deleteUser(userId);
      setSuccessMsg(t('User deleted successfully'));
      setTimeout(() => setSuccessMsg(''), 3000);
      fetchUsers();
    } catch (error) {
      let msg = 'Failed to delete user.';
      if (error?.response?.data?.error) {
        msg = error.response.data.error;
      }
      setErrorMsg(msg);
      console.error('Failed to delete user:', error);
    }
  };

  const handleEditUser = (u) => {
    setEditingUser(u);
    setFormData({
      first_name: u.first_name,
      last_name: u.last_name,
      email: u.email,
      role: u.role || 'user',
      password: '',
    });
    setShowModal(true);
    setErrorMsg('');
  };

  const handleSaveUser = async () => {
    setErrorMsg('');
    try {
      if (editingUser) {
        await adminAPI.updateUser(editingUser.id, formData);
      } else {
        await adminAPI.createUser(formData);
      }
      setShowModal(false);
      setEditingUser(null);
      setFormData({ first_name: '', last_name: '', email: '', role: 'user', password: '' });
      setSuccessMsg(editingUser ? t('User updated successfully') : t('User created successfully'));
      setTimeout(() => setSuccessMsg(''), 3000);
      fetchUsers();
    } catch (error) {
      let msg = 'Failed to save user.';
      if (error?.response?.data?.error) {
        msg = error.response.data.error;
      }
      setErrorMsg(msg);
      console.error('Failed to save user:', error);
    }
  };

  const filteredUsers = users.filter(u =>
    u.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <AdminLayout>
        <div className="min-h-screen bg-[#0B0E14] flex items-center justify-center">
          <div className="text-[#A0AEC0] text-lg">{t('Loading users...')}</div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="min-h-screen bg-[#0B0E14] relative overflow-hidden">
        {/* Background Gradient */}
        <div className="fixed inset-0 bg-gradient-to-br from-[#128C7E]/5 via-transparent to-[#128C7E]/3 pointer-events-none" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
          
          {/* Header Section */}
          <div className="mb-8 sm:mb-10 lg:mb-12">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 sm:gap-6">
              <div>
                <h1 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white mb-1 sm:mb-2">
                  👥 {t('Users Management')}
                </h1>
                <p className="text-[#A0AEC0] text-sm sm:text-lg">
                  {t('Manage all system users and their roles')}
                </p>
              </div>
              <button 
                className="inline-flex items-center justify-center gap-2 bg-gradient-to-br from-[#128C7E] to-[#0DA699] text-white px-6 sm:px-8 py-2 sm:py-3 rounded-lg sm:rounded-xl hover:shadow-lg hover:shadow-[#128C7E]/30 transition-all duration-300 font-semibold active:scale-95 whitespace-nowrap text-sm sm:text-base"
                onClick={() => { 
                  setEditingUser(null); 
                  setFormData({ first_name: '', last_name: '', email: '', role: 'user', password: '' }); 
                  setShowModal(true); 
                  setErrorMsg('');
                }}
              >
                <Plus size={18} className="sm:w-5 sm:h-5" /> <span className="hidden sm:inline">{t('Add New User')}</span><span className="sm:hidden">Add</span>
              </button>
            </div>
          </div>

          {/* Messages */}
          {successMsg && (
            <div className="mb-4 sm:mb-6 bg-[#10B981]/10 border border-[#10B981] text-[#10B981] px-4 sm:px-6 py-3 sm:py-4 rounded-lg sm:rounded-xl flex items-center gap-3 shadow-lg text-sm sm:text-base">
              <CheckCircle size={18} className="sm:w-5 sm:h-5 flex-shrink-0" />
              <span className="font-medium">{successMsg}</span>
            </div>
          )}

          {errorMsg && (
            <div className="mb-4 sm:mb-6 bg-[#EF4444]/10 border border-[#EF4444] text-[#EF4444] px-4 sm:px-6 py-3 sm:py-4 rounded-lg sm:rounded-xl flex items-start gap-3 shadow-lg text-sm sm:text-base">
              <AlertCircle size={18} className="sm:w-5 sm:h-5 flex-shrink-0 mt-0.5" />
              <span className="font-medium">{errorMsg}</span>
            </div>
          )}

          {/* Search & Stats Bar */}
          <div className="mb-6 sm:mb-8 bg-gradient-to-br from-[#1A1F2E] to-[#141820] border border-[#2D3748] rounded-lg sm:rounded-2xl p-4 sm:p-6 shadow-xl">
            <div className="flex flex-col gap-4 sm:gap-6">
              <div className="flex-1 relative">
                <Search className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 text-[#A0AEC0] w-4 sm:w-5 h-4 sm:h-5" size={20} />
                <input
                  type="text"
                  placeholder={t('Search users...')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full bg-[#0B0E14] border border-[#2D3748] text-white pl-9 sm:pl-12 pr-3 sm:pr-4 py-2 sm:py-3 rounded-lg sm:rounded-xl focus:border-[#128C7E] focus:outline-none transition-all placeholder-[#718096] text-sm sm:text-base"
                />
              </div>
              <div className="flex items-center gap-3 sm:gap-4 text-xs sm:text-sm">
                <div className="flex-1 text-center px-3 sm:px-4 py-2 sm:py-3 bg-[#0B0E14]/50 rounded-lg border border-[#2D3748]">
                  <p className="text-[#A0AEC0] text-xs sm:text-sm">{t('Total Users')}</p>
                  <p className="text-xl sm:text-2xl font-bold text-[#128C7E]">{users.length}</p>
                </div>
                <div className="flex-1 text-center px-3 sm:px-4 py-2 sm:py-3 bg-[#0B0E14]/50 rounded-lg border border-[#2D3748]">
                  <p className="text-[#A0AEC0] text-xs sm:text-sm">{t('Showing')}</p>
                  <p className="text-xl sm:text-2xl font-bold text-[#128C7E]">{filteredUsers.length}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Modal - Enhanced */}
          {showModal && (
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-3 sm:p-4">
              <div className="bg-gradient-to-br from-[#1A1F2E] to-[#141820] border border-[#128C7E]/30 rounded-2xl sm:rounded-3xl max-w-xl w-full p-4 sm:p-8 shadow-2xl shadow-[#128C7E]/20">
                <div className="flex items-center justify-between mb-6 sm:mb-8">
                  <h2 className="text-xl sm:text-3xl font-bold text-white">
                    {editingUser ? '✏️ ' + t('Edit User') : '➕ ' + t('Add New User')}
                  </h2>
                  <button
                    onClick={() => setShowModal(false)}
                    className="text-[#A0AEC0] hover:text-white hover:bg-[#2D3748] p-1 sm:p-2 rounded-lg transition-all"
                  >
                    <X size={20} className="sm:w-6 sm:h-6" />
                  </button>
                </div>

                {/* Error in Modal */}
                {errorMsg && (
                  <div className="mb-4 sm:mb-6 bg-[#EF4444]/10 border border-[#EF4444] text-[#EF4444] px-3 sm:px-4 py-2 sm:py-3 rounded-lg sm:rounded-xl flex items-start gap-3 text-sm">
                    <AlertCircle size={16} className="sm:w-5 sm:h-5 flex-shrink-0 mt-0.5" />
                    <span className="text-xs sm:text-sm">{errorMsg}</span>
                  </div>
                )}

                {/* Form Fields */}
                <div className="space-y-5">
                  <div className="grid grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-semibold text-[#A0AEC0] mb-2">
                        {t('First Name')} *
                      </label>
                      <input
                        type="text"
                        value={formData.first_name}
                        onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                        className="w-full bg-[#0B0E14] border border-[#2D3748] text-white px-4 py-3 rounded-lg focus:border-[#128C7E] focus:outline-none focus:ring-2 focus:ring-[#128C7E]/20 transition-all"
                        placeholder={t('John')}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-[#A0AEC0] mb-2">
                        {t('Last Name')} *
                      </label>
                      <input
                        type="text"
                        value={formData.last_name}
                        onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                        className="w-full bg-[#0B0E14] border border-[#2D3748] text-white px-4 py-3 rounded-lg focus:border-[#128C7E] focus:outline-none focus:ring-2 focus:ring-[#128C7E]/20 transition-all"
                        placeholder={t('Doe')}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-[#A0AEC0] mb-2">
                      {t('Email Address')} *
                    </label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className={`w-full bg-[#0B0E14] border border-[#2D3748] text-white px-4 py-3 rounded-lg focus:border-[#128C7E] focus:outline-none focus:ring-2 focus:ring-[#128C7E]/20 transition-all ${editingUser ? 'opacity-60 cursor-not-allowed' : ''}`}
                      placeholder="john@example.com"
                      disabled={!!editingUser}
                    />
                    {editingUser && <p className="text-xs text-[#718096] mt-1">{t('Email cannot be changed')}</p>}
                  </div>

                  <div className="grid grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-semibold text-[#A0AEC0] mb-2">
                        {t('Role')} *
                      </label>
                      <select
                        value={formData.role}
                        onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                        className="w-full bg-[#0B0E14] border border-[#2D3748] text-white px-4 py-3 rounded-lg focus:border-[#128C7E] focus:outline-none focus:ring-2 focus:ring-[#128C7E]/20 transition-all"
                      >
                        <option value="user">{t('Regular User')}</option>
                        <option value="admin">👑 {t('Administrator')}</option>
                      </select>
                    </div>

                    {!editingUser && (
                      <div>
                        <label className="block text-sm font-semibold text-[#A0AEC0] mb-2">
                          {t('Password')} *
                        </label>
                        <input
                          type="password"
                          value={formData.password}
                          onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                          className="w-full bg-[#0B0E14] border border-[#2D3748] text-white px-4 py-3 rounded-lg focus:border-[#128C7E] focus:outline-none focus:ring-2 focus:ring-[#128C7E]/20 transition-all"
                          placeholder="••••••••"
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-4 mt-8">
                  <button
                    className="flex-1 bg-[#2D3748] hover:bg-[#2D3748]/80 text-white px-6 py-3 rounded-lg transition-all font-semibold"
                    onClick={() => setShowModal(false)}
                  >
                    {t('Cancel')}
                  </button>
                  <button
                    className="flex-1 bg-gradient-to-br from-[#128C7E] to-[#0DA699] hover:shadow-lg hover:shadow-[#128C7E]/30 text-white px-6 py-3 rounded-lg transition-all font-semibold active:scale-95"
                    onClick={handleSaveUser}
                  >
                    {editingUser ? t('Update User') : t('Create User')}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Users List - Card View for better UX */}
          {filteredUsers.length === 0 ? (
            <div className="text-center py-20">
              <div className="text-7xl mb-6">📭</div>
              <h3 className="text-3xl font-bold text-white mb-3">{t('No users found')}</h3>
              <p className="text-[#A0AEC0] text-lg mb-8 max-w-md mx-auto">{t('Create your first user or adjust your search filters')}</p>
              <button 
                onClick={() => {
                  setEditingUser(null);
                  setFormData({ first_name: '', last_name: '', email: '', role: 'user', password: '' });
                  setShowModal(true);
                  setErrorMsg('');
                }}
                className="inline-flex items-center gap-2 bg-[#128C7E] text-white px-6 py-3 rounded-xl hover:bg-[#0DA699] transition-all font-semibold"
              >
                <Plus size={20} /> {t('Create First User')}
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4">
              {filteredUsers.map((u) => (
                <div
                  key={u.id}
                  className="group relative bg-gradient-to-br from-[#1A1F2E] to-[#141820] border border-[#2D3748] group-hover:border-[#128C7E]/50 rounded-2xl p-6 transition-all duration-300 hover:shadow-lg hover:shadow-[#128C7E]/10"
                >
                  <div className="flex items-start justify-between gap-6">
                    {/* User Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-4 mb-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-[#128C7E] to-[#0DA699] rounded-full flex items-center justify-center flex-shrink-0 text-white font-bold text-lg">
                          {u.first_name?.charAt(0).toUpperCase()}{u.last_name?.charAt(0).toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <h3 className="text-lg font-bold text-white truncate">
                            {u.first_name} {u.last_name}
                          </h3>
                          <p className="text-[#A0AEC0] text-sm truncate">{u.email}</p>
                        </div>
                      </div>
                      
                      <div className="flex flex-wrap items-center gap-3">
                        <span className={`inline-flex px-3 py-1 rounded-full text-sm font-semibold ${
                          u.role === 'admin' 
                            ? 'bg-[#F59E0B]/10 text-[#F59E0B]' 
                            : 'bg-[#10B981]/10 text-[#10B981]'
                        }`}>
                          {u.role === 'admin' ? '👑 ' : '👤 '}{t(u.role || 'user')}
                        </span>
                        <span className="text-[#718096] text-sm">
                          {t('Created')}: {new Date(u.created_at).toLocaleDateString(i18n.language)}
                        </span>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        className="p-3 bg-[#128C7E]/10 text-[#128C7E] rounded-lg hover:bg-[#128C7E]/20 transition-all group-hover:shadow-md"
                        onClick={() => handleEditUser(u)}
                        title={t('Edit')}
                      >
                        <Edit size={20} />
                      </button>
                      <button
                        className="p-3 bg-[#EF4444]/10 text-[#EF4444] rounded-lg hover:bg-[#EF4444]/20 transition-all group-hover:shadow-md"
                        onClick={() => handleDeleteUser(u.id)}
                        title={t('Delete')}
                      >
                        <Trash2 size={20} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
